'use client'

import { ArrowRight, Plus } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import React, { useState } from 'react'

import { LocaleLink } from '@/components/LocaleLink'
import { cn } from '@/utilities/ui'

export type ItemServico = {
  numero: string
  titulo: string
  texto?: string | null
  href?: string | null
  rotuloLink?: string | null
  imagem?: React.ReactNode
}

type Props = {
  itens: ItemServico[]
  modoMovel?: 'acordeao' | 'lista'
}

/** Lista numerada 01…n com hover (desktop) e acordeão (móvel). */
export function ListaServicos({ itens, modoMovel = 'acordeao' }: Props) {
  const [aberto, setAberto] = useState<number | null>(0)
  const [hover, setHover] = useState<number | null>(null)

  return (
    <div className="isolate flex w-full flex-col">
      {itens.map((item, i) => {
        const Wrapper = (item.href ? LocaleLink : 'div') as React.ElementType
        const wrapperProps = item.href ? { href: item.href } : {}
        const ativo = hover === i
        return (
          <div key={i} className="relative w-full">
            {/* Móvel */}
            <div className="border-b-[0.5px] border-[rgb(var(--sec-fg-rgb)/0.4)] lg:hidden">
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-between py-4 text-left"
                aria-expanded={modoMovel === 'lista' ? undefined : aberto === i}
                onClick={() => modoMovel !== 'lista' && setAberto(aberto === i ? null : i)}
              >
                <span className="flex items-baseline gap-4">
                  <span className="ag-mono text-[var(--sec-muted)]">{item.numero}</span>
                  <span className="ag-heading text-[20px] leading-[1.2]">{item.titulo}</span>
                </span>
                {modoMovel !== 'lista' ? (
                  <Plus
                    className={cn('h-5 w-5 shrink-0 transition-transform duration-300', aberto === i && 'rotate-45')}
                    strokeWidth={1.5}
                  />
                ) : null}
              </button>
              <AnimatePresence initial={false}>
                {(modoMovel === 'lista' || aberto === i) && (
                  <motion.div
                    key="c"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <div className="flex items-start gap-3 pb-4">
                      <div className="flex flex-1 flex-col gap-3">
                        {item.texto ? (
                          <p className="text-sm leading-[1.4] text-[rgb(var(--sec-fg-rgb)/0.87)]">{item.texto}</p>
                        ) : null}
                        {item.href ? (
                          <LocaleLink href={item.href} className="ag-mono flex items-center gap-2 text-[var(--sec-fg)]">
                            {item.rotuloLink || 'Saiba mais'}
                            <ArrowRight className="h-4 w-4" strokeWidth={2} />
                          </LocaleLink>
                        ) : null}
                      </div>
                      {item.imagem ? <div className="relative h-[90px] w-[120px] shrink-0 overflow-hidden">{item.imagem}</div> : null}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop */}
            <Wrapper
              {...(wrapperProps as object)}
              className="group hidden lg:block"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
            >
              <div
                className={cn(
                  'relative flex w-full cursor-pointer flex-col gap-[50px] border-b-[0.5px] border-[rgb(var(--sec-fg-rgb)/0.3)] px-5 py-[50px] transition-colors duration-300',
                  ativo && 'bg-[rgb(var(--sec-fg-rgb)/0.04)]',
                )}
              >
                <div className="flex items-start gap-8">
                  <span className="ag-mono w-8 shrink-0 pt-1 text-[var(--sec-muted)]">{item.numero}</span>
                  <div className="flex flex-1 flex-col gap-4">
                    <div className="flex items-center justify-between gap-6">
                      <h3 className="ag-heading text-[24px] leading-[1.2]">{item.titulo}</h3>
                      <ArrowRight
                        className="h-6 w-6 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-rotate-45"
                        strokeWidth={2}
                      />
                    </div>
                    {item.texto ? (
                      <p className="max-w-[520px] text-[16px] leading-[1.4] text-[rgb(var(--sec-fg-rgb)/0.87)]">{item.texto}</p>
                    ) : null}
                  </div>
                  {item.imagem ? (
                    <motion.div
                      className="relative h-[120px] w-[160px] shrink-0 overflow-hidden"
                      initial={false}
                      animate={{ opacity: ativo ? 1 : 0, scale: ativo ? 1 : 0.95, x: ativo ? 0 : 10 }}
                      transition={{ duration: 0.3 }}
                      aria-hidden
                    >
                      {item.imagem}
                    </motion.div>
                  ) : null}
                </div>
              </div>
            </Wrapper>
          </div>
        )
      })}
    </div>
  )
}
